import React from "react"
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ClipboardCheck, Search, Lightbulb, BookOpen, Users, FileText, BarChart3, FileCheck, } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { SEOHead } from './SEOHead';
import { PageFaq } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema } from '../services/SiteSettings';

const SERVICE_ICONS = [FileCheck, BarChart3, Users, Users, FileText, Search, BarChart3, FileText, Users, BarChart3];
const CAPABILITY_ICONS = [ClipboardCheck, Search, Lightbulb, BookOpen];

export function ExpertisePage() {
  const settings = useSiteSettings();
  const seo = getPageSeo(settings, '/expertise');
  const faqSchema = buildFaqSchema(seo.faqs);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://uspconsultancy.com.np/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Expertise",
        "item": "https://uspconsultancy.com.np/expertise"
      }
    ]
  };
  const services = settings.taxonomy.thematicAreas.map((area, i) => ({
    icon: SERVICE_ICONS[i % SERVICE_ICONS.length],
    title: area.title || area.name,
    description: area.description,
    features: area.subAreas || [],
  }));
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "USP Consultancy Expertise & Services",
    "description": "Comprehensive consulting services offered by USP Consultancy in Nepal",
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Service",
        "name": service.title,
        "description": service.description,
        "provider": {
          "@type": "Organization",
          "name": "USP Consultancy",
          "url": "https://uspconsultancy.com.np/"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Nepal"
        }
      }
    }))
  };

  const expertiseAreas = settings.taxonomy.assignmentTypes.map((type, i) => ({
    icon: CAPABILITY_ICONS[i % CAPABILITY_ICONS.length],
    title: type.name,
    description: type.description,
    features: type.subTypes || [],
  }));

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Expertise & Services'}
        description={seo.description}
        canonical="https://uspconsultancy.com.np/expertise"
        keywords={seo.keywords}
        takeaways={seo.takeaways}
        structuredData={faqSchema ? [breadcrumbSchema, serviceSchema, faqSchema] : [breadcrumbSchema, serviceSchema]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <header className="text-center mb-12">
          <h1 className="mb-4">Our Expertise and Services</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">{seo.description}</p>
        </header>

        {/* Our Services */}
        <section className="mb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="mb-4">Our Services</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                USP Consultancy offers comprehensive services across multiple development sectors, providing evidence-based solutions and technical expertise to drive sustainable development and social transformation.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 h-full flex flex-col">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="mb-2 text-lg font-semibold">{service.title}</h3>
                        <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                      </div>
                    </div>
                    {/* Features / Bullet Points */}
                    {service.features && (
                      <div className="mt-auto space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                            <span className="text-gray-600 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="p-2 text-center">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Our Services By Thematic and SubThematic Area</h3>
                  <div className="w-full h-96 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                    <ImageWithFallback
                      src="/services-thematic.png"
                      alt="USP Consultancy Service Areas - Climate Resilience, Food Security, Poverty Alleviation, Governance, Digital Development, Economic Development, Social Protection, Education"
                      className="w-full h-full object-contain"
                      fallbackSrc="/placeholder-services.png"
                    />
                  </div>
                  {/* <p className="text-gray-600 mt-4 text-sm">
                    Comprehensive development services across multiple sectors including climate resilience, food security, poverty alleviation, and more.
                  </p> */}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="p-2 text-center">
                  <h3 className="text-xl font-semibold mb-4 text-gray-800">Our Solution And Capabilities</h3>
                  <div className="w-full h-96 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                    <ImageWithFallback
                      src="/solution.png"
                      alt="USP Consultancy Service Offerings - Monitoring and Evaluation, Research and Analysis, Project Development, Capacity Development"
                      className="w-full h-full object-contain"
                      fallbackSrc="/placeholder-services.png"
                    />
                  </div>
                  {/* <p className="text-gray-600 mt-4 text-sm">
                    Specialized consulting services including monitoring and evaluation, research and analysis, project development, and capacity building.
                  </p> */}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h2 className="mb-6">Our Solutions and Capabilities</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We provide comprehensive development solutions through evidence-based approaches, rigorous analysis, and capacity building to drive sustainable impact.
          </p>
        </div>

        {/* Expertise Areas */}
        <div className="space-y-12">
          {expertiseAreas.map((area, index) => (
            <Card key={index} className="overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-1 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                      <area.icon className="w-8 h-8" />
                    </div>
                    <div>
                      <h3>{area.title}</h3>
                    </div>
                  </div>
                  <p className="text-primary-foreground/90">
                    {area.description}
                  </p>
                </div>
                <div className="lg:col-span-2 p-8">
                  <h4 className="mb-6">Key Services & Capabilities</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {area.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Additional Information */}
        <section className="mt-16 bg-gray-50 rounded-lg p-8">
          <div className="text-center mb-8">
            <h2 className="mb-4">Why Choose Our Expertise?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our integrated approach and deep domain knowledge ensure comprehensive solutions that address real-world challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {settings.about.stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl text-primary-foreground">{stat.value}</span>
                </div>
                <h3 className="mb-2">{stat.label}</h3>
                <p className="text-gray-600">{stat.description}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>
    </div>
  );
}
